// types
import { createSlice } from '@reduxjs/toolkit';
import config from '../../config';
import { SettingsState } from '../types';
import { FileMetadata } from '../../api/types/cms';
import { Locale } from '../../api/types/translation';

type ChangeLangPayload = {
    lang: string;
};

type ApplyBrandingPayload = {
    system_name: string;
    primary_color: string;
    secondary_color: string;
    frontend_info_endpoint?: string;
    logo?: FileMetadata;
    dark_mode?: boolean;
    locales: Array<Locale>;
};

type SetNetworkErrorPayload = {
    error: boolean;
    hidden?: boolean;
};

// initial state
const initialState: SettingsState = {
    mode: config.mode,
    i18n: config.i18n,
    themeDirection: config.themeDirection,
    networkError: false,
    networkErrorHidden: false,
    defaultTableLength: 15,
    globalSearch: false,
    redirectAfterSave: true,
};

// ==============================|| SLICE - MENU ||============================== //

const settings = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        toggleMode(state, action) {
            if (state.mode === 'light') {
                state.mode = 'dark';
            } else {
                state.mode = 'light';
            }
        },
        setMode(state, action: { payload: 'dark' | 'light' }) {
            state.mode = action.payload;
        },
        changeLanguage(state, action: { payload: ChangeLangPayload }) {
            if (action.payload.lang) {
                state.i18n = action.payload.lang;
            }
        },
        changeDefaultTableLength(state, action: { payload: number }) {
            state.defaultTableLength = action.payload;
        },
        toggleGlobalSearch(state, action: { payload: boolean }) {
            state.globalSearch = action.payload;
        },
        toggleRedirectAfterSave(state, action) {
            state.redirectAfterSave = !state.redirectAfterSave;
        },
        applyBranding(state, action: { payload: ApplyBrandingPayload }) {
            if (action.payload) {
                state.branding = {
                    ...state.branding,
                    ...{
                        systemName: action.payload.system_name,
                        frontendInfoEndpoint:
                            action.payload.frontend_info_endpoint,
                        logo: action.payload.logo,
                        primaryColor: action.payload.primary_color,
                        secondaryColor: action.payload.secondary_color,
                        darkMode: action.payload.dark_mode,
                        locales: action.payload.locales,
                    },
                };
            }
        },
        clearBranding(state) {
            state.branding = undefined;
        },
        setNetworkError(state, action: { payload: SetNetworkErrorPayload }) {
            state.networkError = action.payload.error;
            state.networkErrorHidden = action.payload?.hidden ?? false;
        },
        updateStateByApi(
            state,
            action: { payload: { branding: ApplyBrandingPayload } },
        ) {
            this.applyBranding(state, { payload: action.payload.branding });
        },
    },
});

export default settings.reducer;

export const {
    toggleMode,
    setMode,
    toggleRedirectAfterSave,
    changeDefaultTableLength,
    toggleGlobalSearch,
    changeLanguage,
    applyBranding,
    clearBranding,
    setNetworkError,
} = settings.actions;
